import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, 
  Shield, 
  Handshake, 
  TrendingUp, 
  FolderKanban, 
  Settings, 
  Search, 
  Users, 
  DollarSign,
  ArrowRight 
} from 'lucide-react';
import { mockData } from '../mockData';

const ServiceDetailsPage = () => {
  const { t } = useTranslation();
  const [heroSlide, setHeroSlide] = useState(0);
  
  // Hero carousel images
  const heroSlides = [
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1200&q=90", // Professionals
    "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=90", // Team working
    mockData.drcImages.infrastructure, // DRC infrastructure
    "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?w=1200&q=90"  // Business growth
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  const services = [
    {
      icon: Briefcase,
      title: t('serviceDetails.service1'),
      description: t('serviceDetails.service1Desc')
    },
    {
      icon: Shield,
      title: t('serviceDetails.service2'),
      description: t('serviceDetails.service2Desc')
    },
    {
      icon: Handshake,
      title: t('serviceDetails.service3'),
      description: t('serviceDetails.service3Desc')
    },
    {
      icon: TrendingUp,
      title: t('serviceDetails.service4'),
      description: t('serviceDetails.service4Desc')
    },
    {
      icon: FolderKanban,
      title: t('serviceDetails.service5'),
      description: t('serviceDetails.service5Desc')
    },
    {
      icon: Settings,
      title: t('serviceDetails.service6'),
      description: t('serviceDetails.service6Desc')
    },
    {
      icon: Search,
      title: t('serviceDetails.service7'),
      description: t('serviceDetails.service7Desc')
    },
    {
      icon: Users,
      title: t('serviceDetails.service8'),
      description: t('serviceDetails.service8Desc')
    },
    {
      icon: DollarSign,
      title: t('serviceDetails.service9'),
      description: t('serviceDetails.service9Desc')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Premium Hero Section with Carousel */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[500px]">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={heroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[heroSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-blue-800/60"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content */}
        <div className="max-w-6xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-6 py-2 mb-6">
              {t('serviceDetails.professionalServices')}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{t('serviceDetails.title')}</h1>
            <p className="text-2xl text-blue-100 font-light max-w-3xl mx-auto">
              {t('serviceDetails.heroSubtitle')}
            </p>
            
            {/* Slide Indicators */}
            <div className="flex gap-2 justify-center pt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === heroSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('serviceDetails.ourServices')}</h2>
            <p className="text-xl text-gray-600">{t('serviceDetails.elevateYourBusiness')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-blue-500 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-5 rounded-2xl w-20 h-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-10 h-10 text-blue-900" />
                    </div>
                    <CardTitle className="text-2xl text-blue-900 group-hover:text-blue-700 transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-900 px-6 py-2 mb-4">{t('serviceDetails.videoProfile')}</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('serviceDetails.seeUsInAction')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('serviceDetails.videoDescription')}
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
            <video
              controls
              poster={mockData.drcImages.city1}
              className="w-full h-auto"
            >
              <source src={mockData.videos.english} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 mb-6">
              {t('serviceDetails.videoFooter')}
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white px-10 py-6 text-lg group">
                {t('serviceDetails.getStarted')}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailsPage;
